// ============================================================================
// Página pública com conteúdo: /kb/:tenantSlug/:spaceSlug/:pageSlug
// Renderiza o markdown/HTML publicado, com SEO + Article JSON-LD + citations.
// F3 Bloco A — Wiki pública.
// ============================================================================

import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { Loader2, Calendar } from "lucide-react";
import { useKbPublicPage } from "@/hooks/kb/useKbPublic";
import { KbPublicLayout } from "./KbPublicLayout";

export default function KbPublicPagePage() {
  const { tenantSlug = "", spaceSlug = "", pageSlug = "" } = useParams<{
    tenantSlug: string;
    spaceSlug: string;
    pageSlug: string;
  }>();

  const { data: page, isLoading, error } = useKbPublicPage(
    tenantSlug,
    spaceSlug,
    pageSlug,
  );

  const sanitizedHtml = useMemo(() => {
    if (!page?.content_html) return "";
    // Sanitiza HTML antes de inserir para prevenir XSS.
    return DOMPurify.sanitize(page.content_html, { USE_PROFILES: { html: true } });
  }, [page?.content_html]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const canonical = `${window.location.origin}/kb/${tenantSlug}/${spaceSlug}/${pageSlug}`;

  if (error || !page) {
    return (
      <KbPublicLayout tenantSlug={tenantSlug}>
        <Helmet>
          <title>Página não encontrada</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="rounded-lg border border-border bg-muted/30 p-10 text-center">
          <h1 className="text-2xl font-semibold">Página não encontrada</h1>
          <p className="mt-2 text-muted-foreground">
            Este documento não existe, foi removido ou não está publicado publicamente.
          </p>
        </div>
      </KbPublicLayout>
    );
  }

  const description =
    page.summary?.slice(0, 155) ??
    `Artigo de ${page.tenant_name} — ${page.space_name}.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page.title,
    description,
    datePublished: page.published_at,
    dateModified: page.updated_at,
    inLanguage: page.locale,
    mainEntityOfPage: canonical,
    publisher: {
      "@type": "Organization",
      name: page.tenant_name,
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Base de Conhecimento",
        item: `${window.location.origin}/kb/${tenantSlug}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: page.space_name,
        item: `${window.location.origin}/kb/${tenantSlug}/${spaceSlug}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: page.title,
        item: canonical,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <html lang={page.locale} />
        <title>{`${page.title} — ${page.tenant_name}`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="article:published_time" content={page.published_at ?? ""} />
        <meta property="article:modified_time" content={page.updated_at} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <KbPublicLayout
        tenantSlug={tenantSlug}
        tenantName={page.tenant_name}
        breadcrumbs={[
          { label: "Início", to: `/kb/${tenantSlug}` },
          { label: page.space_name, to: `/kb/${tenantSlug}/${spaceSlug}` },
          { label: page.title },
        ]}
      >
        <article className="prose prose-neutral mx-auto max-w-3xl dark:prose-invert">
          <header className="not-prose mb-8">
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
              {page.title}
            </h1>
            {page.summary && (
              <p className="mt-3 text-lg text-muted-foreground">{page.summary}</p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {page.published_at && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" aria-hidden />
                  Publicado em{" "}
                  {new Date(page.published_at).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              )}
              <span>· v{page.version_number}</span>
            </div>
          </header>

          {/* Conteúdo sanitizado — HTML publicado passa por DOMPurify (XSS guard). */}
          {sanitizedHtml ? (
            <div
              className="kb-public-content"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            <p className="text-muted-foreground">Conteúdo indisponível.</p>
          )}
        </article>
      </KbPublicLayout>
    </>
  );
}
